import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SEO } from '../components/SEO';

const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.simbaherbals.com';

export const Contact = () => {
  const [searchParams] = useSearchParams();
  const packageName = searchParams.get('package');
  const contactPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Us – Simba Herbals UK',
    description: 'Get in touch. Consultations and questions about our remedies. WhatsApp, email, or visit Wolverhampton.',
    url: `${SITE_URL}/contact`,
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'Simba Herbals UK',
      url: SITE_URL,
      telephone: '+44-7724-009805',
      email: 'care@simbaherbals.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '50 Victoria Street',
        addressLocality: 'Wolverhampton',
        postalCode: 'WV1 3PJ',
        addressCountry: 'GB',
      },
      openingHoursSpecification: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
    },
  };

  return (
    <div className="pb-20">
      <SEO
        title="Contact Us"
        description="Get in touch with Simba Herbals UK. Consultations and questions about our remedies. WhatsApp +44 7724 009805, care@simbaherbals.com, or visit 50 Victoria Street, Wolverhampton."
        canonical="/contact"
        jsonLd={contactPageJsonLd}
      />
      <PageHero
        title="Get in Touch"
        subtitle="Start Your Journey. Whether you need a personalized consultation or have questions about our remedies, our expert team is here to guide you."
        image="https://images.unsplash.com/photo-1516387938699-a93567ec168e?auto=format&fit=crop&q=80&w=2000"
        height="h-[100vh]"
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-20 pt-16">
        
        {/* Left: Info */}
        <div className="space-y-12">
          <div className="space-y-8">
            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-simba-green/10 rounded-full flex items-center justify-center text-simba-green flex-shrink-0">
                <MapPin />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-simba-dark mb-1">Visit Us</h3>
                <p className="text-gray-500">50 Victoria Street, Wolverhampton, WV1 3PJ, United Kingdom</p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-simba-green/10 rounded-full flex items-center justify-center text-simba-green flex-shrink-0">
                <Mail />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-simba-dark mb-1">Email Us</h3>
                <p className="text-gray-500">care@simbaherbals.com</p>
                <p className="text-gray-500">consult@simbaherbals.com</p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="w-12 h-12 bg-simba-green/10 rounded-full flex items-center justify-center text-simba-green flex-shrink-0">
                <Phone />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-simba-dark mb-1">Call Us</h3>
                <p className="text-gray-500">+44 7724 009805</p>
                <p className="text-xs text-gray-400 mt-1">Mon-Fri, 9am - 6pm GMT</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="bg-white p-8 md:p-12 rounded-[2rem] shadow-xl border border-gray-100">
          {packageName && (
            <p className="mb-6 py-3 px-4 bg-simba-green/10 text-simba-dark rounded-xl font-medium">
              You're inquiring about: <strong>{decodeURIComponent(packageName)}</strong>
            </p>
          )}
          <h2 className="font-display text-2xl text-simba-dark mb-8">Send us a Message</h2>
          <form
            className="space-y-6"
            action="https://formsubmit.co/hotlinkseo@gmail.com"
            method="POST"
          >
            <input type="hidden" name="_subject" value="New contact form message - Simba Herbals" />
            <input type="hidden" name="_captcha" value="false" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">First Name</label>
                <input type="text" name="first_name" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-simba-green transition-colors" placeholder="John" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-gray-700 ml-1">Last Name</label>
                <input type="text" name="last_name" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-simba-green transition-colors" placeholder="Doe" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 ml-1">Email Address</label>
              <input type="email" name="email" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-simba-green transition-colors" placeholder="john@example.com" required />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 ml-1">Subject</label>
              <select name="subject" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-simba-green transition-colors text-gray-600">
                <option>General Inquiry</option>
                <option>Product Question</option>
                <option>Consultation Request</option>
                <option>Order Support</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 ml-1">Message</label>
              <textarea name="message" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 h-32 focus:outline-none focus:border-simba-green transition-colors resize-none" placeholder="How can we help you?" required></textarea>
            </div>

            <button type="submit" className="w-full bg-simba-green text-white font-bold py-4 rounded-xl hover:bg-simba-dark transition-all flex items-center justify-center shadow-lg hover:shadow-xl transform hover:-translate-y-1">
              Send Message <Send size={18} className="ml-2" />
            </button>
          </form>
        </div>

      </div>
    </div>
  );
};
